import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Store, Package, ShoppingBag, LayoutDashboard, Tag, Wallet } from "lucide-react";
import api from "../../lib/api";
import { fmt } from "../../lib/money";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { WhatsAppIcon } from "../../components/WhatsAppButton";
import { toast } from "sonner";

export default function SellerDashboard() {
  const { user, refresh } = useAuth();
  const location = useLocation();
  const [stats, setStats] = useState({ products: 0, orders: 0, revenue: 0 });
  const [whatsapp, setWhatsapp] = useState("");
  const [savingWa, setSavingWa] = useState(false);

  useEffect(() => {
    if (user?.whatsapp) setWhatsapp(user.whatsapp);
  }, [user?.whatsapp]);

  const saveWhatsapp = async () => {
    setSavingWa(true);
    try {
      await api.put("/seller/profile", { whatsapp });
      await refresh();
      toast.success("WhatsApp updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSavingWa(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [prods, orders] = await Promise.all([
          api.get("/seller/products"),
          api.get("/seller/orders"),
        ]);
        const revenue = orders.data.reduce((s, o) => {
          return s + o.items.filter((i) => i.seller_id === user.user_id).reduce((ss, it) => ss + it.price * it.quantity, 0);
        }, 0);
        setStats({ products: prods.data.length, orders: orders.data.length, revenue: Math.round(revenue * 100) / 100 });
      } catch {}
    })();
  }, [user, location.pathname]);

  const notApproved = user?.role === "seller" && user?.seller_status !== "approved";

  const navCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 text-sm ${isActive ? "bg-white text-black" : "text-neutral-400 hover:text-white"}`;

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-[240px_1fr] bg-white">
      <aside className="bg-[#121212] text-white p-5 lg:sticky lg:top-16 lg:self-start lg:h-[calc(100vh-4rem)]">
        <div className="flex items-center gap-2 font-display font-black text-xl mb-8">
          <Store className="w-5 h-5" /> Seller
        </div>
        <nav className="space-y-1 -mx-4">
          <NavLink end to="/seller" className={navCls} data-testid="seller-nav-overview">
            <LayoutDashboard className="w-4 h-4" /> Overview
          </NavLink>
          <NavLink to="/seller/products" className={navCls} data-testid="seller-nav-products">
            <Package className="w-4 h-4" /> Products
          </NavLink>
          <NavLink to="/seller/orders" className={navCls} data-testid="seller-nav-orders">
            <ShoppingBag className="w-4 h-4" /> Orders
          </NavLink>
          <NavLink to="/seller/earnings" className={navCls} data-testid="seller-nav-earnings">
            <Wallet className="w-4 h-4" /> Earnings
          </NavLink>
          <NavLink to="/seller/discounts" className={navCls} data-testid="seller-nav-discounts">
            <Tag className="w-4 h-4" /> Discounts
          </NavLink>
        </nav>
      </aside>

      <div className="p-4 sm:p-6 lg:p-10 min-w-0">
        {notApproved && (
          <div className="border border-[#FF2A00] bg-[#FF2A00]/5 p-4 mb-6 text-sm">
            Your seller application is <b>{user.seller_status}</b>. You can view your dashboard but cannot list products until approved.
          </div>
        )}

        {location.pathname === "/seller" || location.pathname === "/seller/" ? (
          <div>
            <h1 className="font-display font-black text-4xl tracking-tight">Overview</h1>
            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              {[
                { k: "Products", v: stats.products },
                { k: "Orders", v: stats.orders },
                { k: "Revenue", v: fmt(stats.revenue) },
              ].map((s) => (
                <div key={s.k} className="border border-neutral-200 p-5">
                  <div className="text-xs uppercase tracking-widest text-neutral-500">{s.k}</div>
                  <div className="font-display font-black text-4xl mt-2">{s.v}</div>
                </div>
              ))}
            </div>
            <div className="mt-10 flex gap-3">
              <Link to="/seller/products" className="bg-black text-white px-5 h-11 inline-flex items-center font-semibold">Manage products</Link>
              <Link to="/seller/orders" className="border border-black px-5 h-11 inline-flex items-center font-semibold">View orders</Link>
            </div>

            {/* WhatsApp contact */}
            <div className="mt-12 border border-neutral-200 p-6 max-w-2xl">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 bg-[#25D366] text-white grid place-items-center">
                  <WhatsAppIcon className="w-4 h-4" />
                </div>
                <h2 className="font-display font-bold text-lg">WhatsApp contact</h2>
              </div>
              <p className="text-sm text-neutral-600 max-w-md">
                Show a WhatsApp chat button on your product pages. Buyers tap it and a pre-filled message with the product link opens in WhatsApp.
              </p>
              <div className="mt-4 flex gap-2 max-w-md">
                <Label htmlFor="whatsapp" className="sr-only">WhatsApp number</Label>
                <Input
                  id="whatsapp"
                  data-testid="whatsapp-input"
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  placeholder="e.g. 5926001234 (country code + number)"
                  className="rounded-none border-black h-11"
                />
                <Button
                  data-testid="whatsapp-save-btn"
                  onClick={saveWhatsapp}
                  disabled={savingWa}
                  className="rounded-none bg-[#25D366] hover:bg-[#1ebe5d] h-11 px-5 font-semibold"
                >
                  {savingWa ? "Saving…" : user?.whatsapp ? "Update" : "Save"}
                </Button>
              </div>
              {user?.whatsapp && (
                <p className="mt-3 text-xs text-neutral-500" data-testid="whatsapp-current">
                  Current: <b className="text-black">{user.whatsapp}</b> — the chat button is live on all your product pages.
                </p>
              )}
              <p className="mt-2 text-xs text-neutral-500">
                Include your country code, no `+` or spaces. Leave blank and save to hide the button.
              </p>
            </div>
          </div>
        ) : (
          <Outlet />
        )}
      </div>
    </div>
  );
}
